import { z } from 'zod';

// ============================================
// Base Schemas (Reusable)
// ============================================

export const RiskLevelSchema = z.enum(['Low', 'Medium', 'High']).nullable();
export type RiskLevel = z.infer<typeof RiskLevelSchema>;

// ============================================
// Intelligence Schemas
// ============================================

export const ComplianceItemSchema = z.object({
  name: z.string(),
  web_source_urls: z.array(z.string()),
});

export type ComplianceItem = z.infer<typeof ComplianceItemSchema>;

export const IntelligenceEventSchema = z.object({
  title: z.string(),
  text: z.string(),
  web_source_urls: z.array(z.string()),
});

export type IntelligenceEvent = z.infer<typeof IntelligenceEventSchema>;

// ============================================
// Product Schema
// ============================================

export const VendorProductSchema = z.object({
  id: z.string(),
  type: z.string(),
  name: z.string(),
  product_name: z.string(),
  status: z.string(),
  assessment_step: z.number(),
  total_assessment_steps: z.number().default(5),
  created_by: z.string().nullable(),
  organization_id: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  last_assessed_at: z.string().nullable(),
  risk_rating: z.string().nullable().optional(), // API returns risk_rating, not risk_level
});

export type VendorProduct = z.infer<typeof VendorProductSchema>;

// ============================================
// Main Vendor Detail Schema (Flat structure - matches API response)
// ============================================

export const VendorDetailResponseSchema = z.object({
  // Basic Info
  vendor_name: z.string(),
  company_name: z.string().nullable(),
  founded: z.string().nullable(),
  headquarters: z.string().nullable(),
  ownership: z.string().nullable(),
  no_of_employees: z.string().nullable(),
  primary_domain: z.string().nullable(),
  vendor_logo_url: z.string().nullable(),

  // Content
  services_and_products: z.string().nullable(),
  company_information: z.string().nullable(),

  // Intelligence Arrays
  compliances: z.array(ComplianceItemSchema),
  breach_history_events: z.array(IntelligenceEventSchema),
  dark_web_chatter_findings: z.array(IntelligenceEventSchema),
  news_articles: z.array(IntelligenceEventSchema),

  // Intelligence Counts
  total_compliances: z.number().default(0),
  total_breaches: z.number().default(0),
  total_dark_web_chatter_findings: z.number().default(0),
  total_news_articles: z.number().default(0),

  // Risk Data
  risk_rating: z.string().nullable(),
  overall_risk_score: z.number().nullable(),
  risk_justification: z.string().nullable(),
  vendor_risk_posture: z.object({
    score: z.number(),
    max: z.number(),
  }).passthrough().nullable(), // passthrough() allows extra fields like change, formula

  // Products
  products: z.array(VendorProductSchema),
});

export type VendorDetailResponse = z.infer<typeof VendorDetailResponseSchema>;
export type VendorData = VendorDetailResponse; // Same now - flat structure

// ============================================
// Runtime Validation (API Layer)
// ============================================

export const parseVendorDetailResponse = (data: unknown): VendorDetailResponse => {
  return VendorDetailResponseSchema.parse(data);
};
